const CASE_STUDIES = [
  {
    slug: 'video-analytics',
    title: 'Computer Vision with Cameras',
    shortTitle: 'Computer Vision with Cameras',
    industry: 'Logistics',
    tagline:
      'How Monkstack closed the gap between live camera footage and the WMS — detections on cameras already on site, then intelligent agents that verify, check the system of record, and act.',
    image: '/img/case-studies/video-analytics-poster.jpg',
    videos: [
      {
        src: '/video/video-analytics-detection.mp4',
        poster: '/img/case-studies/video-analytics-detection-poster.jpg',
        alt: 'Computer vision detecting activity from existing cameras',
      },
      {
        src: '/video/video-analytics-agents.mp4',
        poster: '/img/case-studies/video-analytics-poster.jpg',
        alt: 'Computer vision agents reviewing live camera footage',
      },
    ],
    layout: 'article',
    seoDescription:
      'Monkstack built an AI video analytics platform that runs on existing RTSP and ONVIF cameras, then puts intelligent agents between detections and the WMS, ERP, or TMS so exceptions are evidenced and actioned while the event is still happening.',
    overview:
      'A distribution center already runs two separate accounts of the same day. Cameras capture what physically took place on the dock, in the aisles, and at the pack stations. The WMS holds what was planned — the ASN, the pick task, the cycle count, the appointment. When the two disagree, nobody finds out until a customer files a claim, an auditor asks a question, or a detention invoice arrives. We close that gap from both ends: we make the cameras understand what they are looking at, then put intelligent agents between that understanding and the system of record.',
    highlights: [
      {
        label: 'Existing Cameras',
        text: 'Models run against live feeds over standard RTSP and ONVIF. No camera replacement, no change to the recording estate.',
      },
      {
        label: 'Site-Tuned Detections',
        text: 'Each detection is configured per camera, per zone, and per shift — a catalog, not a fixed menu.',
      },
      {
        label: 'Agents, Not Alerts',
        text: 'Detection still ends with a human unless the second layer acts: verify, query WMS/ERP/TMS, then write the exception back.',
      },
      {
        label: 'Evidenced Decisions',
        text: 'Every action carries the camera, timestamp, and clip, filed against the right record while the driver is still in the yard.',
      },
    ],
    problemIntro:
      'Your cameras record what happened. Your systems document what was supposed to happen. But one does not talk to the other:',
    problemPoints: [
      'Safety, fire, security, and housekeeping issues live in footage that nobody watches until after the incident',
      'Dock activity, trailer dwell, and pallet counts are reconstructed from WMS records instead of from what the camera actually saw',
      'Idle time, occupancy, and after-hours movement stay as impressions rather than numbers',
      'Investigators spend hours scrubbing archives for a forklift, a missing spotter, or a blocked exit',
      'A blind, blurred, or tampered camera is often discovered at the next audit, not the same day',
    ],
    problemClose:
      'The site needed cameras that understand the floor, and a layer that can check the system of record and act — without waiting for a claim, an auditor, or a detention invoice.',
    solutionIntro:
      'We built two layers. First, detections on the cameras already owned. Second, intelligent agents that sit between the feed and WMS, ERP, or TMS — each owning a specific operational job, working continuously, and leaving an evidenced trail.',
    solutionSections: [
      {
        number: '01',
        title: 'See — Detection on Live Feeds',
        body: 'ML models run on the live feeds and detect the event as it occurs, with the camera, timestamp, and clip attached. No camera replacement. Detections are selected and tuned to the site during the pilot, and new ones are trained where an operation has a problem the standard set does not cover.',
      },
      {
        number: '02',
        title: 'Verify — Confirm Before Cost Is Incurred',
        body: 'The detection is confirmed automatically, with an optional human review tier for anything that carries a real cost if it is wrong.',
      },
      {
        number: '03',
        title: 'Check — Query the System of Record',
        body: 'An agent queries the WMS, ERP, or TMS. Was this expected? Is there a booked appointment, an open task, an approved contractor?',
      },
      {
        number: '04',
        title: 'Act — Write Back With Evidence',
        body: 'The agent writes back — opens the exception, creates the task, notifies the owner, and files the evidence against the right record. At 14:20 a camera on door 12 picks up unloading activity. The triage agent checks the WMS, finds a booked appointment, and escalates nothing. Twenty-two pallets come off the trailer; the ASN says twenty-four. The dock reconciliation agent opens a receiving exception against the load, attaches the clip showing the final count, notifies the inbound supervisor, and flags the carrier record — while the driver is still in the yard.',
      },
    ],
    catalogTitle: 'The AI Surveillance Platform',
    catalogIntro:
      'This is a catalog, not a fixed menu. Detections are selected and tuned to the site during the pilot.',
    catalog: [
      { label: '01 Safety', text: 'PPE compliance — hard hat, high-visibility vest, safety footwear and gloves, by zone and by shift, with the frame that shows the violation.' },
      { label: '02 Safety', text: 'Forklift and pedestrian separation — movement, speed in restricted aisles, and proximity at blind corners and crossing points.' },
      { label: '03 Fire', text: 'Fire and smoke detection from the video itself — racking aisles, battery-charging areas, and anywhere fixed detectors do not reach.' },
      { label: '04 Security', text: 'Intrusion and restricted zones — perimeter, high-value cages, and controlled doors. Draw a zone; entry outside permitted hours raises an alert.' },
      { label: '05 Operations', text: 'Dock and trailer activity — arrival, door occupancy, loading and unloading, and how long a trailer actually sits before it is worked.' },
      { label: '06 Operations', text: 'Housekeeping and obstruction — blocked fire exits, blocked aisles, and pallets left standing in travel paths.' },
      { label: '07 Labor', text: 'Idle time and station activity — how long a pick face, pack station, or work zone stays inactive across a shift.' },
      { label: '08 Presence', text: 'Headcount and occupancy — live counts by zone and at gates for muster, contractor control, and shift reconciliation.' },
      { label: '09 Security', text: 'Loitering and after-hours movement — dwell where nobody should be dwelling, tailgating, and movement outside operating windows.' },
      { label: '10 Integrity', text: 'Camera health and tamper — blur, blackout, obstruction, and repositioning reported the same day, not at the next audit.' },
      { label: '11 Search', text: 'Ask My CCTV — ask the archive in plain language and get matching clips back, in English and other languages.' },
    ],
    architectureTitle: 'From Detection to Action',
    architecture: [
      {
        domain: 'Camera Estate',
        solution: 'Live inference over existing RTSP and ONVIF feeds, configured per camera, zone, and shift',
        outcome: 'No camera replacement and no change to the recording estate',
      },
      {
        domain: 'Detection Catalog',
        solution: 'Safety, fire, security, operations, labor, presence, integrity, and natural-language search',
        outcome: 'Site-tuned detections, with new models trained where the standard set does not cover the problem',
      },
      {
        domain: 'Agent Layer',
        solution: 'See, verify, check WMS/ERP/TMS, then write back with clip and timestamp',
        outcome: 'Exceptions are opened against the right record while the event is still happening',
      },
      {
        domain: 'Evidence Trail',
        solution: 'Camera, timestamp, and clip attached to every decision the agents make',
        outcome: 'Investigations that used to take hours of scrubbing become a single query',
      },
    ],
    techTitle: 'Built for Live Operations',
    tech: [
      {
        title: 'Vision Layer',
        text: 'Models on live RTSP and ONVIF feeds, with per-camera zones, shift windows, and automatic camera-health monitoring',
      },
      {
        title: 'Agent Layer',
        text: 'Intelligent agents that read WMS, ERP, or TMS for context and write exceptions, tasks, and notifications back',
      },
      {
        title: 'Investigation',
        text: 'Ask My CCTV — plain-language search across the archive that returns matching clips instead of hours of scrubbing',
      },
    ],
  },
  {
    slug: 'ai-erp',
    title: 'AI-Powered ERP for Shop-Floor Manufacturing',
    shortTitle: 'AI ERP',
    industry: 'Manufacturing',
    tagline:
      'How Monkstack replaced delayed floor logs and spreadsheet BOMs with a voice-and-vision ERP that captures production in seconds and locks the perimeter with zero-trust access.',
    image: '/img/higgsfield-preview/erp-control-room.jpg',
    layout: 'article',
    seoDescription:
      'Monkstack built an AI ERP for manufacturing with real-time voice and vision floor logging, a 3-tier BOM, piece-level material traceability, 98% operator adoption, and zero-trust Entra MFA security.',
    productUrl: 'https://precision.monkstack.com/',
    productLabel: 'Visit Monkstack ERP',
    overview:
      'A manufacturing operator asked Monkstack to replace end-of-shift paperwork with an AI ERP that the shop floor would actually use. Production data lived in delayed logs, unstructured spreadsheets, and approximate batch tracking. The platform we shipped captures floor activity in real time through speech and vision, enforces a three-level bill of materials with unit conversion, traces material by piece, weight, and scrap at the room level, and sits behind zero-trust edge IP gating with Microsoft Entra MFA.',
    highlights: [
      {
        label: 'Floor Logging',
        text: 'Real-time capture under 3 seconds via Voice and Vision, versus 2 to 4 hours of end-of-shift delay',
      },
      {
        label: 'BOM Control',
        text: 'Enforced 3-tier hierarchy with unit conversion, replacing unstructured, error-prone spreadsheets',
      },
      {
        label: 'Traceability',
        text: 'Piece, weight, and scrap tracking by room instead of approximate bulk batch tracking',
      },
      {
        label: 'Adoption & Security',
        text: '98% operator adoption through a bilingual speech interface, with zero-trust edge IP gating and Entra MFA',
      },
    ],
    problemIntro:
      'Shop-floor operations were running on tools the plant never designed for manufacturing tempo. Supervisors closed shifts with incomplete data, and planners could not trust what reached the office:',
    problemPoints: [
      'Floor logs arrived 2 to 4 hours late, after the shift had already ended',
      'Bills of materials lived in unstructured spreadsheets with no hierarchy or unit conversion',
      'Material movement was tracked as approximate bulk batches, not by piece, weight, or scrap',
      'Operators avoided English-only forms, so adoption stayed low and records stayed incomplete',
      'The web app sat behind a basic login, with no perimeter control at the plant edge',
    ],
    problemClose:
      'The plant needed an ERP that could be used on the floor in the operator’s language, keep BOM and material data honest, and close the security gap without slowing production.',
    solutionIntro:
      'We did not layer AI on top of a generic ERP. We built the operating system around how this plant actually works: voice and vision on the floor, strict product structure in the office, and a locked perimeter at the edge.',
    solutionSections: [
      {
        number: '01',
        title: 'Voice and Vision Floor Logging',
        body: 'Operators log production as they work. Speech and vision capture replace end-of-shift forms, so floor events reach the ERP in under 3 seconds instead of 2 to 4 hours later.',
      },
      {
        number: '02',
        title: 'Enforced 3-Tier BOM',
        body: 'Product structure is no longer a spreadsheet. The ERP enforces a three-level bill of materials with unit conversion, so configuration errors are caught before they hit the floor.',
      },
      {
        number: '03',
        title: 'Room-Level Material Traceability',
        body: 'Inventory is tracked by piece, weight, and scrap against the room where it is consumed. Planners can see what moved, what was used, and what was wasted — not a bulk batch estimate.',
      },
      {
        number: '04',
        title: 'Bilingual Speech and Zero-Trust Access',
        body: 'A bilingual speech interface drove 98% operator adoption. Access is gated at the edge by IP and completed with Microsoft Entra MFA, replacing an open web app behind a basic login.',
      },
    ],
    metricsTitle: 'Performance',
    metricsColumns: ['Performance Metric', 'Traditional Manual Process', 'AI ERP Platform'],
    metrics: [
      {
        metric: 'Floor Log Latency',
        before: '2 to 4 hours end-of-shift delay',
        after: 'Real-time (under 3 seconds via Voice/Vision)',
      },
      {
        metric: 'BOM Configuration Depth',
        before: 'Unstructured, error-prone spreadsheets',
        after: 'Enforced 3-tier hierarchy with unit conversion',
      },
      {
        metric: 'Material Traceability',
        before: 'Approximate bulk batch tracking',
        after: 'Piece, weight, and scrap tracking by room',
      },
      {
        metric: 'Operator Adoption Rate',
        before: 'Low due to manual English-only forms',
        after: '98% adoption via bilingual speech interface',
      },
      {
        metric: 'Perimeter Security Posture',
        before: 'Open web app behind basic login',
        after: 'Zero-trust edge IP gating and Entra MFA',
      },
    ],
    architectureTitle: 'Architecture & Impact Summary',
    architecture: [
      {
        domain: 'Shop-Floor Capture',
        solution: 'Voice and vision logging into the ERP at the point of work',
        outcome: 'Floor data in under 3 seconds instead of end-of-shift delay',
      },
      {
        domain: 'Product Structure',
        solution: 'Enforced 3-tier BOM with unit conversion',
        outcome: 'Configuration errors stop before they reach production',
      },
      {
        domain: 'Inventory Integrity',
        solution: 'Piece, weight, and scrap tracking by room',
        outcome: 'True material traceability instead of bulk estimates',
      },
      {
        domain: 'Access Control',
        solution: 'Zero-trust edge IP gating with Microsoft Entra MFA',
        outcome: 'The plant perimeter is closed without blocking operators who belong there',
      },
    ],
    techTitle: 'Built for Modern Manufacturing',
    tech: [
      {
        title: 'Shop-Floor Interface',
        text: 'Bilingual speech capture, vision logging, and a modular web ERP used at the machine — not after the shift',
      },
      {
        title: 'Identity & Security',
        text: 'Microsoft Entra MFA, zero-trust edge IP gating, and least-privilege plant access',
      },
      {
        title: 'Manufacturing Data',
        text: '3-tier BOM with unit conversion; piece, weight, and scrap traceability by room',
      },
    ],
  },
  {
    slug: 'construction-takeoff-saas',
    title: 'Multi-Tenant Construction Management & 2D-to-3D Plan Takeoff SaaS',
    shortTitle: 'Construction Takeoff SaaS',
    industry: 'PropTech',
    tagline:
      'How Monkstack engineered an enterprise-grade, dual-plane B2B PropTech platform for General Contractors, Subcontractors, and their clients.',
    image: '/img/higgsfield-preview/construction-editorial.jpg',
    layout: 'article',
    seoDescription:
      'Monkstack engineered a multi-tenant construction management SaaS with browser-based digital takeoff, automated 2D-to-3D conversion, Microsoft Entra ID, and isolated tenant workspaces for General Contractors and Subcontractors.',
    overview:
      'A high-growth PropTech venture partnered with Monkstack to build a unified web platform for the commercial construction sector. The goal was straightforward: eliminate the friction between architectural plan measurements, spatial visualization, and multi-party project execution. The solution is a high-performance, multi-tenant SaaS that combines browser-based digital takeoff, automated 2D-to-3D drawing conversion, and complete organizational data isolation across competing trade contractors.',
    highlights: [
      {
        label: 'Architecture',
        text: 'Decoupled Control Plane (Super Admin) and Data Plane (Tenant Workspaces)',
      },
      {
        label: 'Performance',
        text: 'Sub-second workspace switching with cross-tenant identity mapping',
      },
      {
        label: 'Spatial Processing',
        text: 'Cloud-native pipeline converting 2D vector plans into interactive 3D models',
      },
      {
        label: 'Security & Auth',
        text: 'Microsoft Entra ID integration with granular role-based access control (RBAC)',
      },
    ],
    problemIntro:
      'Commercial construction delivery is notoriously fragmented. General Contractors (GCs), Subcontractors (SCs), estimators, and owners routinely juggle disconnected tools:',
    problemPoints: [
      'Heavy desktop takeoff software that locks measurements into local files',
      'Flat 2D blueprints that make spatial coordination and clash detection slow and error-prone',
      'Messy email threads and shared drives with zero permission controls between competing subcontractors',
      'Complex commercial operations where onboarding new enterprise accounts requires manual database setups',
    ],
    problemClose:
      'Our client needed a modern, browser-native platform capable of running precise plan takeoffs, rendering complex spatial conversions in real time, and maintaining airtight tenant isolation for thousands of concurrent companies.',
    solutionIntro:
      'Here’s the thing: you cannot build a platform that serves both enterprise SaaS operators and field-level project managers with a single flat architecture. We engineered a dual-plane ecosystem designed for scale, speed, and clean operational boundaries.',
    solutionSections: [
      {
        number: '01',
        title: 'Dual-Plane Architecture',
        body: 'We split the platform into two dedicated application layers:',
        points: [
          'Control Plane (/platform/...): A centralized command console for SaaS administrators to onboard GC organizations, manage commercial product catalogs, configure feature tiers, monitor platform-level finances, and inspect system telemetry.',
          'Data Plane (/tenant/{tenantId}/...): An isolated workspace environment for each company. General Contractors manage their internal project managers, invite trade-specific subcontractors, onboard project owners with scoped read-only visibility, and run active jobs.',
        ],
      },
      {
        number: '02',
        title: 'Browser-Native Digital Takeoff Engine',
        body: 'We designed a canvas-based measurement workspace that processes high-resolution architectural PDF sheets directly in the browser.',
        points: [
          'Calibrates custom drawing scales across imperial and metric coordinate systems',
          'Computes surface areas, linear boundaries, and material counts instantly',
          'Aggregates quantities directly into live estimation tables, removing manual data entry',
        ],
      },
      {
        number: '03',
        title: 'Automated 2D-to-3D Conversion Pipeline',
        body: 'Flat floor plans often hide structural conflicts. We integrated an asynchronous processing engine that parses vector geometries from 2D architectural sheets and reconstructs them into interactive 3D models. Estimators and project leads can jump between 2D sheets and 3D floor assemblies to validate structural spans, run floor-by-floor comparisons, and accelerate design approvals.',
        image: '/img/higgsfield-preview/floorplan-to-3d.jpg',
        imageAlt: 'Architectural model rising from a 2D floor plan',
      },
      {
        number: '04',
        title: 'Cross-Tenant Identity & Workspaces',
        body: 'Real-world subcontractors work with multiple general contractors simultaneously. Rather than forcing users into siloed credentials, we integrated Microsoft Entra ID paired with a global membership broker. Users log in once and select which tenant workspace to enter. Security policies and role-based permissions resolve dynamically at the session edge, preventing cross-tenant data leakage while keeping the user experience seamless.',
      },
    ],
    architecture: [
      {
        domain: 'System Tenancy',
        solution: 'Isolated workspace data plane coupled to an operational control plane',
        outcome: 'Clean separation between SaaS ops, GC management, and subcontractor work',
      },
      {
        domain: 'Vector Processing',
        solution: 'Real-time browser canvas calibration and measurement extraction',
        outcome: 'Zero reliance on legacy desktop takeoff tools; accessible anywhere',
      },
      {
        domain: 'Spatial Engine',
        solution: 'Asynchronous cloud pipeline outputting WebGL 3D spatial models',
        outcome: 'Faster stakeholder sign-offs and earlier trade clash identification',
      },
      {
        domain: 'Monetization',
        solution: 'Dynamic catalog gating with subscription tier enforcement',
        outcome: 'Self-service prospect onboarding and smooth plan upgrades',
      },
    ],
    techTitle: 'Built for Modern PropTech',
    tech: [
      {
        title: 'Frontend',
        text: 'High-performance modular web application, interactive 2D canvas engine, WebGL 3D model viewer',
      },
      {
        title: 'Identity & Security',
        text: 'Microsoft Entra ID, enterprise SSO, dynamic tenant routing, RBAC enforcement',
      },
      {
        title: 'Cloud & Pipeline',
        text: 'Event-driven microservices, asynchronous file ingestion, distributed vector calculation',
      },
    ],
  }
];

export const getCaseStudyBySlug = (slug) =>
  CASE_STUDIES.find((study) => study.slug === slug);

export default CASE_STUDIES;
